import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { InfoIcon } from '@phosphor-icons/react';
import {
  IManualEntryFieldSelected,
  ManualEntryScopeType,
} from '../../../../models/m6-2';
import {
  useManualEntryPreferences,
  useUpsertManualEntryPreferences,
} from '../../hooks/useManualEntryPreferences';

// ─── Field catalog (hardcoded mock per Sprint 5, integrazione backend futura) ───

interface IFieldDef {
  canonicalKey: string;
  label: string;
  level: 'session' | 'per_athlete';
  category: FieldCategory;
  required?: boolean;
}

type FieldCategory =
  | 'Metadati'
  | 'Carico interno'
  | 'Volume'
  | 'Intensità'
  | 'Metabolico'
  | 'Note e percezione';

const FIELD_CATALOG: IFieldDef[] = [
  // Metadati required
  { canonicalKey: 'date', label: 'Data', level: 'session', category: 'Metadati', required: true },
  { canonicalKey: 'duration', label: 'Durata (min)', level: 'session', category: 'Metadati', required: true },

  // Carico interno
  { canonicalKey: 'rpe', label: 'RPE (0-10)', level: 'per_athlete', category: 'Carico interno' },
  { canonicalKey: 'srpe', label: 'sRPE', level: 'session', category: 'Carico interno' },
  { canonicalKey: 'session_load', label: 'Session Load', level: 'session', category: 'Carico interno' },

  // Volume
  { canonicalKey: 'distance_km', label: 'Distanza (km)', level: 'session', category: 'Volume' },
  { canonicalKey: 'steps', label: 'Passi', level: 'session', category: 'Volume' },
  { canonicalKey: 'sets', label: 'Serie', level: 'session', category: 'Volume' },
  { canonicalKey: 'reps', label: 'Ripetizioni', level: 'session', category: 'Volume' },

  // Intensità
  { canonicalKey: 'hr_avg', label: 'FC media (bpm)', level: 'session', category: 'Intensità' },
  { canonicalKey: 'hr_max', label: 'FC max (bpm)', level: 'session', category: 'Intensità' },
  { canonicalKey: 'speed_avg_kmh', label: 'Velocità media (km/h)', level: 'session', category: 'Intensità' },
  { canonicalKey: 'power_avg_w', label: 'Potenza media (W)', level: 'session', category: 'Intensità' },

  // Metabolico
  { canonicalKey: 'kcal', label: 'Kcal', level: 'session', category: 'Metabolico' },
  { canonicalKey: 'vo2max_estimate', label: 'VO2max stimato', level: 'session', category: 'Metabolico' },

  // Note e percezione
  { canonicalKey: 'notes', label: 'Note', level: 'session', category: 'Note e percezione' },
  { canonicalKey: 'wellness_pre', label: 'Benessere pre-sessione (0-10)', level: 'per_athlete', category: 'Note e percezione' },
  { canonicalKey: 'wellness_post', label: 'Benessere post-sessione (0-10)', level: 'per_athlete', category: 'Note e percezione' },
];

const CATEGORY_ORDER: FieldCategory[] = [
  'Metadati',
  'Carico interno',
  'Volume',
  'Intensità',
  'Metabolico',
  'Note e percezione',
];

// ─── Props ────────────────────────────────────────────────────────────

interface ManualEntryFieldSelectorProps {
  /** Tipo scope per le preferenze (team o individual_athlete) */
  scopeType: ManualEntryScopeType;
  /** ID del team o atleta specifico */
  scopeId: string;
  /** Label contestuale (nome team o atleta) per il banner preferenze */
  scopeLabel?: string;
  /** Callback invocato con i campi confermati */
  onConfirm: (fields: IManualEntryFieldSelected[]) => void;
}

// ─── Component ────────────────────────────────────────────────────────

/**
 * Step A wizard input manuale M6.2 B13 — selezione campi con checklist categorizzata.
 * Legge preferenze con useManualEntryPreferences e pre-spunta i campi salvati.
 * Banner "Configurazione consueta" se useCount > 0.
 * I campi Metadati required sono sempre spuntati e non disabilitabili.
 */
export const ManualEntryFieldSelector: React.FC<ManualEntryFieldSelectorProps> = ({
  scopeType,
  scopeId,
  scopeLabel,
  onConfirm,
}) => {
  const { data: preferences, isLoading } = useManualEntryPreferences(scopeType, scopeId);
  const upsert = useUpsertManualEntryPreferences();

  const defaultSelected = useCallback((): Set<string> => {
    const required = FIELD_CATALOG
      .filter((f) => f.required)
      .map((f) => f.canonicalKey);
    return new Set(required);
  }, []);

  const [selected, setSelected] = useState<Set<string>>(defaultSelected);

  // Pre-spunta fields dalle preferenze quando arrivano
  useEffect(() => {
    if (!preferences) return;
    const saved = preferences.fieldsSelected.map((f) => f.canonicalKey);
    const required = FIELD_CATALOG.filter((f) => f.required).map((f) => f.canonicalKey);
    setSelected(new Set([...required, ...saved]));
  }, [preferences]);

  const toggle = useCallback((key: string, required: boolean) => {
    if (required) return; // i required non si possono deselezionare
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    const fields: IManualEntryFieldSelected[] = FIELD_CATALOG
      .filter((f) => selected.has(f.canonicalKey))
      .map((f) => ({ canonicalKey: f.canonicalKey, level: f.level }));

    // Salva preferenze (non blocca — fire-and-forget)
    upsert.mutate({ scopeType, scopeId, fieldsSelected: fields });

    onConfirm(fields);
  }, [selected, scopeType, scopeId, onConfirm, upsert]);

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    fields: FIELD_CATALOG.filter((f) => f.category === cat),
  }));

  const hasPreferences = !!preferences && preferences.useCount > 0;
  const contextLabel = scopeLabel ?? (scopeType === 'team' ? 'questo team' : 'questo atleta');

  return (
    <VStack align="stretch" gap={4}>
      {/* Banner preferenze salvate */}
      {hasPreferences && !isLoading && (
        <HStack
          bg="blue.50"
          borderWidth={1}
          borderColor="blue.200"
          borderRadius="md"
          p={3}
          gap={2}
          align="flex-start"
        >
          <Box color="blue.600" mt="1px">
            <InfoIcon size={16} />
          </Box>
          <Text fontSize="sm" color="blue.800">
            Configurazione consueta per <strong>{contextLabel}</strong> ({preferences.useCount} utilizzi). I campi pre-selezionati riflettono le preferenze salvate.
          </Text>
        </HStack>
      )}

      {/* Checklist categorizzata */}
      {grouped.map(({ category, fields }) => (
        <Box key={category}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={2}
          >
            {category}
          </Text>

          <VStack
            align="stretch"
            gap={0}
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="md"
            overflow="hidden"
          >
            {fields.map((field, idx) => {
              const isRequired = !!field.required;
              const isChecked = selected.has(field.canonicalKey);

              return (
                <HStack
                  key={field.canonicalKey}
                  px={3}
                  py={2}
                  gap={3}
                  align="center"
                  bg={isChecked ? 'green.50' : 'white'}
                  borderTopWidth={idx > 0 ? 1 : 0}
                  borderTopColor="gray.100"
                  cursor={isRequired ? 'not-allowed' : 'pointer'}
                  onClick={() => toggle(field.canonicalKey, isRequired)}
                  _hover={{ bg: isRequired ? undefined : 'gray.50' }}
                >
                  <Checkbox.Root
                    checked={isChecked}
                    disabled={isRequired}
                    onCheckedChange={() => toggle(field.canonicalKey, isRequired)}
                    size="sm"
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                  </Checkbox.Root>

                  <VStack align="flex-start" gap={0} flex={1}>
                    <HStack gap={1} align="center">
                      <Text fontSize="sm" fontWeight={isRequired ? 'semibold' : 'normal'}>
                        {field.label}
                      </Text>
                      {isRequired && (
                        <Text fontSize="xs" color="red.400" fontWeight="bold">
                          *
                        </Text>
                      )}
                    </HStack>
                    <Text fontSize="xs" color="gray.400">
                      {field.level === 'per_athlete' ? 'Per atleta' : 'A livello sessione'}
                    </Text>
                  </VStack>
                </HStack>
              );
            })}
          </VStack>
        </Box>
      ))}

      {/* CTA */}
      <Button
        onClick={handleConfirm}
        colorPalette="green"
        size="md"
        loading={upsert.isPending}
        loadingText="Salvataggio..."
      >
        Usa questa configurazione ({selected.size} campi)
      </Button>
    </VStack>
  );
};
